import { type FC, type SVGProps, type ForwardRefExoticComponent } from 'react';
import type { StrictOmit } from 'src/utils/utility-types';

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const sizeMap: Record<IconSize, { width: string; height: string }> = {
  xs: { width: '12px', height: '12px' },
  sm: { width: '16px', height: '16px' },
  md: { width: '24px', height: '24px' },
  lg: { width: '32px', height: '32px' },
  xl: { width: '48px', height: '48px' },
};

type WithSize = StrictOmit<SVGProps<SVGSVGElement>, 'width' | 'height'> & {
  /**
   * - `xs`: 12px × 12px
   * - `sm`: 16px × 16px
   * - `md`: 24px × 24px
   * - `lg`: 32px × 32px
   * - `xl`: 48px × 48px
   */
  size: IconSize;
  width?: never;
  height?: never;
};

type WithDimensions = StrictOmit<
  SVGProps<SVGSVGElement>,
  'width' | 'height'
> & {
  size?: never;
  width: string;
  height: string;
};

export type IconProps = WithSize | WithDimensions;

type IconComponent = ForwardRefExoticComponent<SVGProps<SVGSVGElement>>;

export const Icon: FC<IconProps & { icon: IconComponent }> = ({
  size,
  icon: IconComponent,
  width,
  height,
  ...props
}) => {
  const computedSize = size ? sizeMap[size] : { width, height };

  return <IconComponent {...computedSize} {...props} aria-hidden />;
};
